/**
 * 「按书录入 / 按书看字表」第一步 —— **出版社 + 年级 + 学期**的纯函数（2026-10-18 定稿）。
 *
 * ## 用户定稿（本轮原话）
 *
 * > 除了**出版社需要建立**外，其他的"**年级**"和"**学期**"这两个是**预设的**：
 * > 1. 年级：从小学一年级到小学六年级。
 * > 2. 学期：就是上册和下册，这两个是**不需要去新建的**，全部就预设好就可以了。
 * > **只有出版社需要建立**，如果没有就要去建立，有的话直接选择。
 * >
 * > 假设我要新建一本书：选择出版社（例如"人民教育出版社"）→ 选择年级（例如"小学一年级"）
 * > → 选择学期（例如"上册"）。**这时候就可以录入了。** 录入"识字表"并保存之后，
 * > 将它**关联到所选出版社、年级和学期的这本书上**。
 * > 我们所说的"书"就是由以下三个要素构成的：1. 出版社 2. 年级 3. 学期
 *
 * ## 界面最终长这样（三格，**都始终可选**）
 *
 * ```text
 *   出版社 [ 人民教育出版社 ▾ ]   年级 [ 小学一年级 ▾ ]   学期 [ 下册 ▾ ]
 *            └ 最后一项：＋ 添加出版社
 * ```
 *
 *   · **出版社下拉**：课本列表里**去重后**的出版社名（⛔ 不带年级 / 学期 / 统计）；
 *     最后一项是**动作**「＋ 添加出版社」（不是一册书）；
 *   · **年级 / 学期**：都是**预设值**（`GRADES` 与上 / 下 / 不分），
 *     ⛔ 没有"新建年级 / 新建学期"任何入口；
 *   · 三格一起决定"是哪一册"：
 *       - 已经有这一册 → 三张表显示它的内容；
 *       - **书还没建过 → 照常能粘、能预览、能导入**，保存时**自动建好**那本书
 *         （见 `resolveBooksImportTarget` 与后端 `POST .../lists/import`）。
 *     ⛔ **不显示任何"缺书"提示**，也**没有任何"建一册"入口**：
 *     用户不需要"建一册"这个概念 —— 书是保存时顺带建出来的。
 *
 * ## 为什么"选中出版社"不再自动落到第一册
 *
 * 旧方案选一家出版社就自动落到"第一册"，再把年级 / 学期锁死 ——
 * 用户要录**一年级下册**时会被卡死（上册的册被选中，学期又改不了）。
 * 所以现在**不做任何自动落册**：出版社只是一个维度，落哪一册由三格共同决定。
 * 换学期 = 换一册（有就切过去，没有就等保存时自动建）。
 *
 * ## 「＋ 添加出版社」是一个**动作**，不是值
 *
 * 它排在最后，值是一个不会与真实出版社撞车的哨兵（`ADD_PUBLISHER_VALUE`）。
 * 选中它时：**不改** `selection` 里的任何值（`resolvePublisherPick` 返回 `{ kind: 'add' }`），
 * 只打开浮窗；下拉是受控的（`value` = 当前选中的出版社）⇒ 显示值不会停在它上面。
 */

import {
  GRADE_CODES,
  gradeLabel,
  publisherDisplayNameOf,
  publisherRefKeyOf,
  semesterTitle,
  textbookRefKey,
} from '@fht/shared';

/** 下拉最后那一项「＋ 添加出版社」的 option value（不会与出版社名撞车）。 */
export const ADD_PUBLISHER_VALUE = '__fht_add_publisher__';

/** 下拉最后那一项的字面。 */
export const ADD_PUBLISHER_LABEL = '＋ 添加出版社';

/** 空值选项的两种文字（有出版社 / 一条都没有）。 */
export const PUBLISHER_PLACEHOLDER_READY = '请选择出版社';
export const PUBLISHER_PLACEHOLDER_EMPTY = '还没有出版社';

/**
 * 还没选中出版社时，年级 / 学期的**默认值**（预设值的第一项）。
 *
 * ⚠️ 它们同时是「＋ 添加出版社」登记时用的年级 / 学期（那是三格里的一部分），
 *    也是三格没改过时"这一册"的默认落点。这两格**始终可选**。
 */
export const DEFAULT_BOOKS_GRADE = 'primary_1';
export const DEFAULT_BOOKS_SEMESTER = '上';

/** 这家出版社下"哪一册排第一"（年级按 `GRADES` 的顺序，学期按 上 → 下 → 不分）。 */
const SEMESTER_ORDER: Record<string, number> = { 上: 0, 下: 1, 不分: 2 };

/** 这一页用得到的课本字段（`TextbookView` 的结构子集，单测里好造）。 */
export interface PublisherTextbookLike {
  id: string;
  publisher: string;
  name: string;
  gradeCode: string;
  semester: string;
}

/**
 * 一本书在「出版社」下拉里显示成什么。
 *
 * 实现只有一份：`@fht/shared` 的 `publisherDisplayNameOf()` —— 历史行
 * （`publisher = pub_xxxxxxxx`、人话在 `name`）显示人话，新数据直接用 `publisher`。
 * ⛔ 界面上**永远**不该出现 `pub_` 那串代号（2026-10-18 生产 bug 的直接症状）。
 */
export function publisherDisplayName(
  textbook: Pick<PublisherTextbookLike, 'publisher' | 'name'>,
): string {
  return publisherDisplayNameOf(textbook);
}

/** 一家出版社在下拉里的**稳定键**（空白 / 大小写不算区别，与库里的口径同一套）。 */
export function publisherOptionValue(
  textbook: Pick<PublisherTextbookLike, 'publisher' | 'name'>,
): string {
  return publisherRefKeyOf(textbook);
}

/** 下拉里一个出版社选项。 */
export interface PublisherOption {
  /** option value（稳定键）。 */
  value: string;
  /** 显示的人话（只有出版社名，⛔ 不带年级 / 学期 / 统计）。 */
  label: string;
  /** 这家出版社下的册 id（已按"第一册优先"排好；空数组不会出现）。 */
  textbookIds: string[];
}

/** "第一册优先"的排序：年级 → 学期 → id（id 只为让结果确定）。 */
function volumeOrder(a: PublisherTextbookLike, b: PublisherTextbookLike): number {
  const gradeCodes = GRADE_CODES as readonly string[];
  const gradeA = gradeCodes.indexOf(a.gradeCode);
  const gradeB = gradeCodes.indexOf(b.gradeCode);
  if (gradeA !== gradeB) {
    return (gradeA === -1 ? gradeCodes.length : gradeA) - (gradeB === -1 ? gradeCodes.length : gradeB);
  }
  const semesterA = SEMESTER_ORDER[a.semester] ?? 99;
  const semesterB = SEMESTER_ORDER[b.semester] ?? 99;
  if (semesterA !== semesterB) return semesterA - semesterB;
  return a.id.localeCompare(b.id);
}

/**
 * 出版社下拉的**真实选项**（不含占位项、不含最后那个动作项）。
 *
 * 口径：
 *   · **去重**：按 `publisherOptionValue`（空白 / 大小写归一）——同一家的两册只有一个选项；
 *   · **排序**：按显示名 `localeCompare(zh-CN)`（界面上一眼能按拼音找到）；
 *   · **label 只有出版社名**：⛔ 不带年级 / 学期 / 字数统计（用户原话"只显示出版社"）；
 *   · 空串出版社不列（那不是"一个出版社"）；
 *   · 课本列表拿不到（`null`）→ 空数组。
 */
export function listPublisherOptions(
  textbooks: readonly PublisherTextbookLike[] | null | undefined,
): PublisherOption[] {
  const groups = new Map<string, { labels: string[]; textbooks: PublisherTextbookLike[] }>();
  for (const textbook of textbooks ?? []) {
    const label = publisherDisplayName(textbook);
    const value = textbookRefKey(label);
    if (value === '') continue;
    const group = groups.get(value);
    if (group === undefined) {
      groups.set(value, { labels: [label], textbooks: [textbook] });
    } else {
      group.labels.push(label);
      group.textbooks.push(textbook);
    }
  }

  const options: PublisherOption[] = [];
  for (const [value, group] of groups) {
    /** 同一组里可能有"人民教育出版社 / 人民教育 出版社"两种写法：取排序后第一个当标签。 */
    const label = [...group.labels].sort((a, b) => a.localeCompare(b, 'zh-CN'))[0] ?? value;
    options.push({
      value,
      label,
      textbookIds: [...group.textbooks].sort(volumeOrder).map((textbook) => textbook.id),
    });
  }
  return options.sort((a, b) => a.label.localeCompare(b.label, 'zh-CN'));
}

/** 下拉里**真正渲染**的选项：占位项 → 去重后的出版社 → （维护模式）最后那个动作项。 */
export interface PublisherSelectOption {
  value: string;
  label: string;
}

export function publisherSelectOptions(
  textbooks: readonly PublisherTextbookLike[] | null | undefined,
  context: { placeholder: string; canAdd: boolean },
): PublisherSelectOption[] {
  const options: PublisherSelectOption[] = [
    { value: '', label: context.placeholder },
    ...listPublisherOptions(textbooks).map((option) => ({
      value: option.value,
      label: option.label,
    })),
  ];
  if (context.canAdd) options.push({ value: ADD_PUBLISHER_VALUE, label: ADD_PUBLISHER_LABEL });
  return options;
}

/** 「出版社 + 年级 + 学期」三格（选择状态里与"哪一册"有关的部分）。 */
export interface PublisherVolumeQuery {
  publisher: string;
  gradeCode: string;
  semester: string;
}

/**
 * **这一组三格对应哪一册**（找不到就是 `null`）。
 *
 * ⚠️ 判据是"出版社显示名键 + 年级 + 学期"三者全等；少一格都返回 `null`
 *    （年级 / 学期下拉里的空值项 = 还没选好，不是"第一册"）。
 * ⚠️ 找不到时**不要**拿这家出版社的第一册兜底：那正是"录下册却写进上册"的根因。
 * ⚠️ `null` **不是错误**、也**不是"不能录"**：三格齐了就照常能录，
 *    保存时由后端自动建出这一册（`resolveBooksImportTarget`）。
 * ⚠️ 理论上同一组三格只会有一行（库里的唯一键），真有多行时按 `volumeOrder` 取第一个，
 *    让结果确定。
 */
export function findPublisherVolume<T extends PublisherTextbookLike>(
  textbooks: readonly T[] | null | undefined,
  query: PublisherVolumeQuery,
): T | null {
  if (query.publisher === '' || query.gradeCode.trim() === '' || query.semester.trim() === '') {
    return null;
  }
  const gradeCode = query.gradeCode.trim();
  const semester = query.semester.trim();
  const matches = (textbooks ?? []).filter(
    (textbook) =>
      publisherOptionValue(textbook) === query.publisher &&
      textbook.gradeCode === gradeCode &&
      textbook.semester === semester,
  );
  if (matches.length === 0) return null;
  return [...matches].sort(volumeOrder)[0] ?? null;
}

/**
 * 下拉值 → 到底发生什么（**纯函数：界面只照着做，测试照着断言**）。
 *
 *   · `add`       —— 选的是最后那个动作项：**不改任何值**，只打开浮窗；
 *   · `clear`     —— 选回占位项：清掉出版社（年级 / 学期保留，它们始终可选）；
 *   · `publisher` —— 选中某一家出版社（不自动落册，等三格一起定位）；
 *   · `unknown`   —— 值不认识（列表换过了 / 被人改过）：**什么都不动**。
 */
export type PublisherPick =
  | { kind: 'add' }
  | { kind: 'clear' }
  | { kind: 'publisher'; publisher: string }
  | { kind: 'unknown' };

/** 把出版社下拉的当前值翻译成"该怎么改选择状态"。 */
export function resolvePublisherPick(
  textbooks: readonly PublisherTextbookLike[] | null | undefined,
  value: string,
): PublisherPick {
  if (value === ADD_PUBLISHER_VALUE) return { kind: 'add' };
  if (value === '') return { kind: 'clear' };
  const option = listPublisherOptions(textbooks).find((item) => item.value === value);
  if (option === undefined) return { kind: 'unknown' };
  return { kind: 'publisher', publisher: option.value };
}

/** 建成功 / 重复之后，选择状态要变成的那三格。 */
export interface BooksVolumeSelection {
  publisher: string;
  gradeCode: string;
  semester: string;
}

/**
 * 把一册课本变成选择状态的那三格。
 *
 * ⚠️ 「添加出版社」**建成功**与**撞上已有那一册**走的是同一条路：
 *    对用户来说只有一件好事 —— **这一册被定位到了**，年级 / 学期跟着它。
 *    创建那条路传新记录，重复那条路传 `findExistingTextbook()` 找到的旧记录。
 * ⚠️ 这里**不返回 `textbookId`**：这一页的真相是"出版社 + 年级 + 学期"，
 *    具体那一册由 `findPublisherVolume()` 现算（列表刷新后不会指向旧 id）。
 */
export function selectionOfVolume(textbook: PublisherTextbookLike): BooksVolumeSelection {
  return {
    publisher: publisherOptionValue(textbook),
    gradeCode: textbook.gradeCode,
    semester: textbook.semester,
  };
}

// ---------------------------------------------------------------- 四态（加载 / 没拿到 / 空 / 有）

/** 「出版社」下拉现在的状态（文案与是否禁用）。 */
export interface PublisherPickerState {
  state: 'loading' | 'missing' | 'empty' | 'ready';
  /** 空值选项的文字（占位项）。 */
  placeholder: string;
  /** 下拉是否**禁止展开**。 */
  disabled: boolean;
  /** 下拉下面那行小字（`null` = 不显示）。 */
  hint: string | null;
  /** 紧邻处的就地指引（`null` = 不显示）。 */
  guide: { message: string } | null;
}

/**
 * 四态判定（**纯函数，有单测**）。
 *
 * ⚠️ `empty` 时**维护模式不下拉禁用**：最后那一项「＋ 添加出版社」是空部署里
 *    登记第一家出版社的入口 —— 禁用等于把入口关掉。
 *    （家长端仍然禁用：他没有那个动作项，展开一个只有占位项的列表只会让人以为坏了。）
 * ⚠️ `loading` 排在 `missing` 前面：加载中列表也是 `null`，先说"没拿到"会闪假错误。
 * ⚠️ 「没有出版社」与「列表没拿到」是两件事，两句文案必须分开。
 */
export function describePublisherPicker(input: {
  textbooks: readonly unknown[] | null | undefined;
  loading: boolean;
  missing: boolean;
  canEdit: boolean;
}): PublisherPickerState {
  const { textbooks, loading, missing, canEdit } = input;

  if (textbooks === null || textbooks === undefined) {
    if (loading) {
      return {
        state: 'loading',
        placeholder: PUBLISHER_PLACEHOLDER_READY,
        disabled: true,
        hint: '正在取出版社列表……',
        guide: null,
      };
    }
    return {
      state: 'missing',
      placeholder: PUBLISHER_PLACEHOLDER_READY,
      disabled: true,
      hint: missing
        ? '课本列表没拿到（不是"你还没有出版社"）—— 请刷新重试'
        : '没拿到课本列表 —— 请刷新重试',
      guide: null,
    };
  }

  if (textbooks.length === 0) {
    return {
      state: 'empty',
      placeholder: PUBLISHER_PLACEHOLDER_EMPTY,
      disabled: !canEdit,
      hint: canEdit ? '点开下拉，最后一项是「＋ 添加出版社」' : '还没有课本（等管理员录入）',
      guide: canEdit
        ? {
            message:
              '还没有出版社 —— 先在下面选好年级和学期，再点开下拉选「＋ 添加出版社」，' +
              '把这家的名字登记进来；之后三格选好就能直接录识字表 / 写字表 / 词语表' +
              '（保存时会自动归到「出版社 + 年级 + 学期」这本书上）。',
          }
        : null,
    };
  }

  return {
    state: 'ready',
    placeholder: PUBLISHER_PLACEHOLDER_READY,
    disabled: false,
    hint: null,
    guide: null,
  };
}

/**
 * 出版社键（下拉的 value）→ 那串**人话**。
 *
 * 下拉里存的是 `publisherOptionValue()` 算出来的稳定键；提交给后端时要用**人话**
 * （服务层按 `publisher` 或 `name` 那一列的人话匹配，代号形状的 `pub_xxxxxxxx`
 * 不会出现在界面文案里）。空键 = 空串。
 */
export function publisherLabelOf(
  textbooks: readonly PublisherTextbookLike[] | null | undefined,
  publisherKey: string,
): string {
  if (publisherKey === '') return '';
  const option = listPublisherOptions(textbooks).find((item) => item.value === publisherKey);
  return option?.label ?? publisherKey;
}

/** 年级 / 学期那两格的小字（本轮：三格一起决定一册；没有这一册就保存时自动建）。 */
export const PICK_VOLUME_HINT = '出版社、年级、学期一起决定这一册（还没有就保存时自动建）';

/** 选好的"这一册"（给弹窗里那句"会建在哪一册"用）：`小学一年级 · 上册`。 */
export function describeSelectedVolume(gradeCode: string, semester: string): string {
  return [
    gradeCode === '' ? '' : gradeLabel(gradeCode),
    semester === '' ? '' : semesterTitle(semester),
  ]
    .filter((part) => part !== '')
    .join(' · ');
}

/**
 * 这一批字表要发去哪本书（`POST .../lists/import` 的"书"那一半）。
 *
 * 用户定稿："这时候就可以录入了。录入识字表并保存之后，将它关联到所选出版社、
 * 年级和学期的这本书上。" —— 所以**书存不存在不影响能不能录**：
 *
 *   · 三格定位到了已有那一册 → 发 `textbookId`（最精确，绝不会落错）；
 *   · **书还没建过** → 只发 `publisher`（人话）+ 年级 + 学期；
 *     后端按这三格解析，**提交时不存在就建**（预览一行都不写、也不建）。
 *
 * 返回 `null` = 三格没齐（出版社 / 年级 / 学期有空的）—— 这时**不该发请求**，
 * 因为落点还没确定。`null` 不是"没有这一册"，是"还没选好"。
 */
export interface BooksImportTarget {
  /** 已有那一册的 id（没有就不带这个键）。 */
  textbookId?: string;
  /** 出版社**人话**（书还没有时用它 + 年级 + 学期让后端解析 / 新建）。 */
  publisher?: string;
  gradeCode: string;
  semester: string;
}

export function resolveBooksImportTarget(input: {
  /** 三格解析到的那一册的 id（`null` = 书还没建过）。 */
  activeTextbookId: string | null;
  /** 当前选中出版社的人话（`publisherLabelOf()`）。 */
  publisherLabel: string;
  gradeCode: string;
  semester: string;
}): BooksImportTarget | null {
  const gradeCode = input.gradeCode.trim();
  const semester = input.semester.trim();
  const publisher = input.publisherLabel.trim();
  if (gradeCode === '' || semester === '' || publisher === '') return null;
  if (input.activeTextbookId !== null && input.activeTextbookId !== '') {
    return { textbookId: input.activeTextbookId, gradeCode, semester };
  }
  return { publisher, gradeCode, semester };
}
